/**
 * CookieConsent Component
 * GDPR-compliant cookie consent banner with Accept, Decline, and Manage Preferences options
 * Mobile-optimized with compact design and proper touch targets
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Link,
  Paper,
  Container,
  IconButton,
} from '@mui/material';
import { Close, Settings, Check, Close as CloseIcon } from '@mui/icons-material';

function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [preferenceSet, setPreferenceSet] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const hasConsent = localStorage.getItem('cookieConsent');
    if (!hasConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => {
        setOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', 'all');
    setPreferenceSet(true);
    setOpen(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setPreferenceSet(true);
    setOpen(false);
  };

  const handleManagePreferences = () => {
    // For now, this opens the cookie policy
    // In a full implementation, this would open a preferences modal
    window.location.href = '/cookie-policy';
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (!open || preferenceSet) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        '@media (max-width: 767px)': {
          maxHeight: '120px',
          overflowY: 'auto',
        },
      }}
    >
      <Container maxWidth="lg">
        <Paper
          elevation={6}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: { xs: 1, sm: 2 },
            borderRadius: { xs: 2, sm: 3 },
            bgcolor: 'grey.900',
            color: 'white',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 2, sm: 3 },
            maxWidth: '1200px',
            mx: 'auto',
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              minWidth: '48px',
              minHeight: '48px',
              '@media (max-width: 767px)': {
                top: 4,
                right: 4,
              },
            }}
            aria-label="Close cookie consent banner"
          >
            <CloseIcon />
          </IconButton>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              pr: { xs: 0, sm: 6 }, // Space for close button on desktop
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '14px', sm: '15px' },
                lineHeight: 1.5,
                mb: 1,
              }}
            >
              We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '13px', sm: '14px' },
                lineHeight: 1.5,
                color: 'grey.300',
              }}
            >
              By clicking "Accept All", you consent to our use of cookies. You can{' '}
              <Link
                href="/cookie-policy"
                color="inherit"
                underline="hover"
                sx={{
                  textDecoration: 'underline',
                  '&:hover': { color: 'grey.100' },
                }}
              >
                learn more
              </Link>{' '}
              or manage your preferences.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1.5, sm: 2 },
              minWidth: { xs: 'auto', sm: '280px' },
            }}
          >
            {/* Decline Button - Secondary styling */}
            <Button
              variant="outlined"
              onClick={handleDecline}
              startIcon={<Close />}
              aria-label="Decline all cookies"
              sx={{
                color: 'white',
                borderColor: 'grey.600',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: '120px',
                minHeight: '48px',
                px: 3,
                py: 1.5,
                fontSize: { xs: '14px', sm: '15px' },
                '&:hover': {
                  borderColor: 'grey.400',
                  bgcolor: 'grey.800',
                },
                '@media (max-width: 767px)': {
                  minWidth: '100%',
                  order: 2,
                },
              }}
            >
              Decline
            </Button>

            {/* Accept All Button - Primary styling */}
            <Button
              variant="contained"
              onClick={handleAcceptAll}
              startIcon={<Check />}
              aria-label="Accept all cookies"
              sx={{
                bgcolor: '#4CAF50',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: '120px',
                minHeight: '48px',
                px: 3,
                py: 1.5,
                fontSize: { xs: '14px', sm: '15px' },
                '&:hover': {
                  bgcolor: '#45a049',
                },
                '@media (max-width: 767px)': {
                  minWidth: '100%',
                  order: 1,
                },
              }}
            >
              Accept All
            </Button>

            {/* Manage Preferences Link */}
            <Button
              variant="text"
              onClick={handleManagePreferences}
              startIcon={<Settings />}
              aria-label="Manage cookie preferences"
              sx={{
                color: 'grey.300',
                fontWeight: 500,
                textTransform: 'none',
                minWidth: { xs: '100%', sm: 'auto' },
                minHeight: '48px',
                px: 2,
                py: 1.5,
                fontSize: { xs: '13px', sm: '14px' },
                '&:hover': {
                  color: 'white',
                  bgcolor: 'transparent',
                },
                '@media (max-width: 767px)': {
                  order: 3,
                  mt: 0.5,
                },
              }}
            >
              Manage Preferences
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default CookieConsent;
